import { hasCapability, tierRequiresAds } from "./capabilities";
export function resolvePatTier(pat) {
    if (!pat)
        return "free";
    const parts = pat.split("_");
    if (parts.length >= 2)
        return planToTier(parts[1]);
    return "free";
}
export function planToTier(plan) {
    if (plan === "internal")
        return "internal";
    if (plan === "custom")
        return "custom";
    if (plan === "enterprise")
        return "enterprise";
    if (plan === "pro")
        return "pro";
    if (plan === "starter")
        return "starter";
    if (plan === "free")
        return "free";
    return "none";
}
export function hasMinTier(current, required) {
    return capabilitiesOrder().indexOf(current) >= capabilitiesOrder().indexOf(required);
}
function capabilitiesOrder() {
    return ["none", "free", "starter", "pro", "enterprise", "custom", "internal"];
}
/**
 * Tiers that are required to display the free-tier advertisement dialog.
 * Single source of truth for ad visibility — resolved from the PAT tier,
 * NOT from any widget-provided flag, so the widget cannot disable it.
 *
 * `none` cũng thấy ads: chưa xác minh ⇒ fail-closed (không cấp quyền).
 */
export const FREE_AD_TIERS = ["free", "starter"];
/**
 * Whether the given PAT tier must show the free-tier ad dialog.
 * Enforced in the engine (closed package); the widget has no input here.
 *
 * Delegated to `tierRequiresAds` (capabilities.ts) — SSOT `no_ads`.
 */
export function requiresAds(tier) {
    return tierRequiresAds(tier);
}
/**
 * Drawing tool types that were the `smart_drawing_pro` Pro set (TIER-16, 2026-09-23).
 *
 * KEPT as the registry of that set, NOT as a gate. On 2026-10-22
 * (`smart_drawing_pro` was lowered to Community — `capabilities.ts`, decision D2 of
 * `PLAN_DEV_PHASE_OFFICIAL_2026-10-22.md`) the tier threshold moved to `"none"`, so
 * `isPremiumDrawingBlocked()` returns false for every tier and the list below no
 * longer withholds anything. It was kept because:
 *   - `engine-drawing/src/drawingTierUtils.ts` still reads it to decide "is this one of
 *     the advanced tools?" (the UI grouping, not an entitlement), and
 *   - it is the regression lock: `patTier.test.ts` asserts this exact set is present
 *     AND that none of it is blocked at any tier, so a silent re-promotion to Pro
 *     fails the engine suite.
 *
 * TIER-16 (2026-09-23): 21 common tools (gannFan, ordinary pitchfork, ordinary Fib,
 * measuring, annotations, H&S...) were already returned to Free to attract users.
 */
export const PREMIUM_DRAWING_TYPES = [
    "gannBox", "gannSquareFixed", "gannSquare",
    "schiffPitchfork",
    "xabcdPattern", "cypherPattern",
    "elliottImpulse", "elliottCorrection", "elliottTriangle",
    "elliottDoubleCombo", "elliottTripleCombo",
    "fibArc", "fibCircles", "fibSpeedResistanceArcs",
    "fibSpiral", "fibSpeedResistanceFan",
];
/**
 * Single source of truth for the drawing-tier check in the engine pipeline.
 * Returns true if the given drawing type should be BLOCKED (not rendered, not hit-tested)
 * for the current user tier.
 *
 * Since 2026-10-22 this returns **false for every tier**: the threshold is
 * `hasCapability(tier, "smart_drawing_pro")`, and that capability is Community
 * (`capabilities.ts` — minTier `"none"`), so the check is satisfied even at the
 * unverified `none` tier. The function and its four call sites are KEPT because they
 * are the place a future Pro drawing set would plug in, and because deleting them
 * would mean re-adding them later in a hurry.
 *
 * Used at three pipeline stages:
 * 1. `computeDrawingPrimitivesState()` — filter before projection (skip wasted CPU)
 * 2. `hitTestDrawingPrimitive()` — skip premium types during click/hover detection
 * 3. `renderOverlayLoop()` — skip premium types during canvas painting
 *
 * Threshold resolved via `hasCapability(..., "smart_drawing_pro")` — SSOT `capabilities.ts`.
 */
export function isPremiumDrawingBlocked(type, tier) {
    if (hasCapability(tier, "smart_drawing_pro"))
        return false;
    return PREMIUM_DRAWING_TYPES.includes(type);
}
